import { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import { Text } from '../../ui-components';
import { formatPhoneNumber } from '../../utils/phone-utils';
import { maxLength, regex } from '../../utils/validation';
import type { Attendee } from '../../pages/cart/models/Attendee';
import type { FieldConfig } from '../field';

const messages = defineMessages({
    required: { defaultMessage: '{field} is required', id: 'error.field.required' },
    invalidFormat: { defaultMessage: 'Enter a valid {field}', id: 'error.field.invalid' },
    maxLength: {
        defaultMessage: 'No more than {max} characters',
        id: 'error.field.maxLength',
    },
    noSpecialChars: {
        defaultMessage: 'No special characters',
        id: 'error.field.no.special.characters',
    },

    firstName: { defaultMessage: 'First Name', id: 'cart.edit-culinary.first_name' },
    lastName: { defaultMessage: 'Last Name', id: 'cart.edit-culinary.last_name' },
    phone: { defaultMessage: 'Phone Number', id: 'cart.edit-culinary.phone' },
    email: {
        defaultMessage: 'Email',
        id: 'cart.edit-culinary.email',
    },
    checkTermsField: {
        defaultMessage:
            'By checking this box, you agree to Sur La Table’s cooking class terms and conditions, including cancellation policies.',
        id: 'cart.edit-culinary.checkTermsField',
    },
    checkTermsRequired: {
        defaultMessage: 'You must agree to the terms & conditions to add or update an attendee',
        id: 'cart.edit-culinary.checkTermsRequired',
    },
});

export function useEditAttendeesCartFields(
    form: UseFormReturn<Attendee>,
): Record<string, FieldConfig> {
    const {
        control,
        formState: { errors },
    } = form;
    const intl = useIntl();

    const fields: Record<string, FieldConfig> = {
        firstName: {
            labelRef: useRef(),
            id: 'attendee-first-name-field',
            name: 'fname',
            label: intl.formatMessage(messages.firstName),
            defaultValue: '',
            variant: 'new-style',
            type: 'text',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'First name' }),
                maxLength: {
                    value: maxLength.firstName,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.firstName,
                    }),
                },
                pattern: {
                    value: regex.firstName,
                    message: intl.formatMessage(messages.noSpecialChars),
                },
            },
            error: errors.fname,
            control,
        },
        lastName: {
            labelRef: useRef(),
            id: 'attendee-last-name-field',
            name: 'lname',
            label: intl.formatMessage(messages.lastName),
            defaultValue: '',
            type: 'text',
            variant: 'new-style',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'Last name' }),
                maxLength: {
                    value: maxLength.lastName,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.lastName,
                    }),
                },
                pattern: {
                    value: regex.lastName,
                    message: intl.formatMessage(messages.noSpecialChars),
                },
            },
            error: errors.lname,
            control,
        },
        email: {
            labelRef: useRef(),
            id: 'attendee-email-field',
            name: 'email',
            autoComplete: 'email',
            type: 'email',
            variant: 'new-style',
            label: intl.formatMessage(messages.email),
            defaultValue: '',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'Email address' }),
                pattern: {
                    value: regex.email,
                    message: intl.formatMessage(messages.invalidFormat, { field: 'email address' }),
                },
            },
            error: errors.email,
            control,
        },
        phone: {
            labelRef: useRef(),
            id: 'attendee-phone-field',
            name: 'phone',
            autoComplete: 'tel',
            label: intl.formatMessage(messages.phone),
            defaultValue: '',
            variant: 'new-style',
            type: 'tel',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'Phone number' }),
                pattern: {
                    value: regex.phone,
                    message: intl.formatMessage(messages.invalidFormat, { field: 'phone number' }),
                },
            },
            error: errors.phone,
            inputProps: ({ onChange }: { onChange: (phone: string) => void }) => ({
                inputMode: 'numeric',
                onChange(evt: React.ChangeEvent<HTMLInputElement>): void {
                    onChange(formatPhoneNumber(evt.target.value));
                },
            }),
            control,
        },
        checkTermsField: {
            id: 'attendee-check-terms',
            name: 'checkTermsField',
            label: <Text size="small">{intl.formatMessage(messages.checkTermsField)}</Text>,
            type: 'checkbox',
            defaultValue: false,
            error: errors.checkTermsField,
            control,
            variant: 'new-style',
            rules: {
                required: intl.formatMessage(messages.checkTermsRequired),
            },
        },
    };

    return fields;
}
